import { isEmpty } from '@shared/common'
import type { RestrictionAdminDto, RestrictionClientDto, RestrictionRelationAdminDto } from 'service-api/src/types/api'
import { DOW, dayToJSDate, formatAmPm, isInRange } from './dates'
import type { TDaysOfWeek } from './dates.d'

export const restrictionForSpecie = (specieId?: string | null, restrictions?: RestrictionClientDto[]) => {
  if (!specieId || !restrictions) {
    return []
  }
  return restrictions.filter((r) => r.restrictions?.some((s) => s.type === 'SPECIES' && s.text === specieId))
}

export const getThresholds = (restrictions?: RestrictionClientDto[], specieId?: string) => {
  if (!restrictions || !specieId) {
    return
  }

  const specieRestriction = restrictions.filter(
    (r) => !(isEmpty(r.min) && isEmpty(r.max)) && (r.restrictions?.findIndex((rr) => rr.text === specieId) ?? -1) >= 0
  )

  if (specieRestriction.length > 0) {
    // note: min/max are inverted
    return {
      min: specieRestriction[0].max || null,
      max: specieRestriction[0].min || null,
    }
  }

  return
}

export const isSelectedDateInBookingRestrictions = (
  restrictions: RestrictionClientDto[],
  selectedDate: string | undefined
): boolean => {
  if (!selectedDate || !restrictions?.length) {
    return false
  }
  const dateStr = selectedDate.length >= 10 ? selectedDate.slice(0, 10) : selectedDate
  const selectedDay = DOW.getDow(dayToJSDate(dateStr))
  return restrictions.some((restriction) => restriction.periods?.some((period) => period.dayOfWeek === selectedDay))
}

export const isSelectedTimeInPeriodRestrictions = (
  restrictions: RestrictionClientDto[],
  dayOfWeek: TDaysOfWeek,
  time: string
): boolean =>
  restrictions.some((restriction) =>
    restriction.periods?.some((period) => {
      return period.dayOfWeek === dayOfWeek && period.startTime && period.endTime
        ? time >= period.startTime && time < period.endTime
        : false
    })
  )

export const isSelectedDateInPeriodRestrictionsRelation = (
  restrictionRelations: RestrictionRelationAdminDto[],
  dayOfWeek: TDaysOfWeek,
  date: string
): boolean =>
  restrictionRelations.some((relation) =>
    relation.restriction?.periods?.some((period) => {
      const startDate = dayToJSDate(period.startDate?.substring(0, 10))?.toISOString()
      const endDate = dayToJSDate(period.endDate?.substring(0, 10))?.toISOString()
      const selectedDate = dayToJSDate(date)?.toISOString()

      return period.dayOfWeek === dayOfWeek && startDate && endDate && selectedDate
        ? startDate <= selectedDate && selectedDate <= endDate
        : period.dayOfWeek === dayOfWeek || false
    })
  )

export const getClockBoundsForDayOfWeek = (
  restrictions: RestrictionClientDto[],
  dayOfWeek: TDaysOfWeek
): { minStart: string; maxEnd: string } | null => {
  const spans: { startTime: string; endTime: string }[] = []
  restrictions.forEach((restriction) => {
    restriction.periods?.forEach((period) => {
      if (period.dayOfWeek === dayOfWeek && period.startTime && period.endTime) {
        spans.push({ startTime: period.startTime, endTime: period.endTime })
      }
    })
  })
  if (!spans.length) {
    return null
  }
  const sortedStarts = [...spans.map((s) => s.startTime)].sort()
  const sortedEnds = [...spans.map((s) => s.endTime)].sort()
  return {
    minStart: sortedStarts[0]!,
    maxEnd: sortedEnds[sortedEnds.length - 1]!,
  }
}

export const isSpecialPeriodForDate = (
  period: NonNullable<RestrictionClientDto['periods']>[number],
  date?: string
): boolean => (!!period.startDate || !!period.endDate) && !!date && isInRange(date, [period])

export const displayHoursInPeriodRestrictions = (
  restrictions: RestrictionClientDto[],
  day: TDaysOfWeek,
  date?: string
): string => {
  const periodsForDay = [] as { startTime: string; endTime: string }[]

  // A special dated period covering the date overrides the regular weekly hours for that date.
  const hasSpecialForDate = restrictions.some((restriction) =>
    restriction.periods?.some((period) => isSpecialPeriodForDate(period, date))
  )

  restrictions.forEach((restriction) => {
    restriction.periods?.forEach((period) => {
      const matches = hasSpecialForDate ? isSpecialPeriodForDate(period, date) : period.dayOfWeek === day
      if (matches && period.startTime && period.endTime) {
        periodsForDay.push({
          startTime: period.startTime,
          endTime: period.endTime,
        })
      }
    })
  })

  periodsForDay.sort((a, b) => {
    if (a.startTime === b.startTime) return a.endTime.localeCompare(b.endTime)
    return a.startTime.localeCompare(b.startTime)
  })

  const displayHours = periodsForDay.map((period) => `${formatAmPm(period.startTime)}-${formatAmPm(period.endTime)}`)

  return displayHours.join(', ')
}

export const temperTestAheadTime = (restriction?: RestrictionAdminDto | RestrictionClientDto): number => {
  if (!restriction) {
    return 0
  }
  return Math.round((restriction.max ?? 0) / 60 / 24)
}
